import {
  redirect,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunction,
} from "react-router";

import { currentSubscriptionQuery } from "@hooks/queries/clinic/useFetchCurrentSubscriptionQuery";
import { dietitianAccountQuery } from "@hooks/queries/settings/useFetchDietitianAccountQuery";
import { systemNotificationsQuery } from "@hooks/queries/useFetchSystemNotificationsQuery";
import { isSubscriptionUsable } from "@hooks/useClinicSubscription";
import { queryClient } from "@services/queryClient";
import i18n from "@services/i18n";
import { Roles } from "@utils/settings";
import {
  CLINIC_SETTINGS_PLAN,
  CLINIC_SETTINGS_PLAN_INVOICES,
  DASHBOARD,
  LOGIN,
  PLANS_AND_PAYMENTS,
  PLANS_AND_PAYMENTS_INVOICES,
  PRICING,
  PRICING_CONFIGURE,
  PRICING_SERVICE_CONFIGURE,
} from "@routes/routes";

export const DIETITIAN_ROLES = [
  Roles.DIETITIAN_OWNER,
  Roles.DIETITIAN_WORKER,
  Roles.DIETITIAN_ASSISTANT,
];

const SUBSCRIPTION_FREE_PATHS = [
  CLINIC_SETTINGS_PLAN,
  CLINIC_SETTINGS_PLAN_INVOICES,
  PLANS_AND_PAYMENTS,
  PRICING,
  PRICING_CONFIGURE,
  PRICING_SERVICE_CONFIGURE,
  PLANS_AND_PAYMENTS_INVOICES,
];

export const revalidateOnPathChange: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
}) => currentUrl.pathname !== nextUrl.pathname;

export const protectedLoader =
  (allowedRoles: Roles[]) =>
  async ({ request }: LoaderFunctionArgs) => {
    const [account, subscription] = await Promise.all([
      queryClient.ensureQueryData(dietitianAccountQuery).catch(() => null),
      queryClient.ensureQueryData(currentSubscriptionQuery).catch(() => null),
      queryClient.ensureQueryData(systemNotificationsQuery).catch(() => null),
    ]);

    const user = account?.data;
    if (!user) throw redirect(LOGIN);
    if (!allowedRoles.some(role => user.roles.includes(role)))
      throw redirect(DASHBOARD);

    const { pathname } = new URL(request.url);
    if (
      !SUBSCRIPTION_FREE_PATHS.includes(pathname) &&
      !isSubscriptionUsable(subscription?.data)
    )
      throw redirect(CLINIC_SETTINGS_PLAN);

    if (user.language && i18n.language !== user.language)
      await i18n.changeLanguage(user.language);

    return null;
  };
